import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Card, Spinner,
} from 'react-bootstrap';
import Moment from 'react-moment';
import { PageLayout } from '../../../../dorian-shared/types/pageLayout/PageLayout';
import {
  Promotion,
  PromotionDetailResponse,
  PromotionSubcategory,
} from '../types/PromotionTypes';
import { api } from '../../../api';
import { ToolComponentProps } from '../../ToolPage/toolComponentProps';
import { PageWrapper } from '../../../ui/PageWrapper';
import { showToast } from '../../../ui/utils';
import { ContestEditor } from '../categories/Contests/ContestEditor';
import { RewardBannerEditor } from '../categories/RewardBanner/RewardBannerEditor';
import { SalesEditor } from '../categories/Sales/SalesEditor';
import { PromotionForm, PromotionFormValues } from '../components/PromotionForm/PromotionForm';
import { PromotionStatusBadge } from '../components/PromotionStatusBadge/PromotionStatusBadge';
import { PromotionTypeTabs } from '../components/PromotionTypeTabs/PromotionTypeTabs';
import {
  CONTESTS_PROMO_TYPE_KEY,
  REWARDS_PROMO_TYPE_KEY,
  SALES_PROMO_TYPE_KEY,
  canEditPromotions,
  getPromotionsListUrl,
} from '../utils/promotionUtils';

export function PromotionOverviewPage(props: ToolComponentProps) {
  const {
    auth, history, match, toolKey, toolRole,
  } = props;
  const user = auth.getUser();
  const isEditor = canEditPromotions(toolRole);
  const promotionId = match.params.id;
  const listUrl = getPromotionsListUrl(toolKey);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [promotion, setPromotion] = useState<Promotion | null>(null);
  const [subcategories, setSubcategories] = useState<PromotionSubcategory[]>([]);
  const [hasUnsavedPromoTypeFields, setHasUnsavedPromoTypeFields] = useState(false);
  const [dirtyPromoTypeKeys, setDirtyPromoTypeKeys] = useState<string[]>([]);

  const pageLayout: PageLayout = {
    header: { title: 'Promotions' },
    sidebar: {
      nav: [
        {
          title: 'Back',
          action: () => history.push(listUrl),
          variant: 'secondary',
          disabled: false,
        },
      ],
    },
  };

  const loadPromotion = useCallback(() => {
    setLoading(true);
    setError(null);
    api.get<PromotionDetailResponse>(`/v1/tools/promotions/${promotionId}`)
      .then((response) => {
        setPromotion(response.data.promotion);
        setSubcategories(response.data.subcategories);
      })
      .catch((err) => {
        setError(err?.response?.data?.error || 'Failed to load promotion');
        setPromotion(null);
      })
      .finally(() => setLoading(false));
  }, [promotionId]);

  useEffect(() => {
    loadPromotion();
  }, [loadPromotion]);

  const handlePromoTypeDirtyChange = useCallback((key: string, dirty: boolean) => {
    setDirtyPromoTypeKeys((current) => {
      const hasKey = current.includes(key);
      if (dirty === hasKey) {
        return current;
      }
      return dirty ? [...current, key] : current.filter((item) => item !== key);
    });
  }, []);

  const handleSubmit = (values: PromotionFormValues) => {
    setSaving(true);
    api.patch(`/v1/tools/promotions/${promotionId}`, values)
      .then((response) => {
        setPromotion(response.data.promotion);
        showToast({ textMessage: 'Promotion saved', variant: 'success' });
      })
      .catch((err) => {
        showToast({
          textMessage: err?.response?.data?.error || 'Failed to save promotion',
          variant: 'danger',
        });
      })
      .finally(() => setSaving(false));
  };

  const isDraft = promotion && (!promotion.startTime || !promotion.endTime);

  let bodyContent;
  if (loading) {
    bodyContent = (
      <div className="text-center py-4">
        <Spinner animation="border" />
      </div>
    );
  } else if (error) {
    bodyContent = <Alert variant="danger">{error}</Alert>;
  } else if (promotion) {
    bodyContent = (
      <>
        <Card className="mt-3">
          <Card.Body>
            <Card.Title className="d-flex align-items-center flex-wrap">
              <span className="mr-2">{promotion.title}</span>
              <PromotionStatusBadge status={promotion.status} />
            </Card.Title>

            {isDraft && isEditor && (
              <Alert variant="warning">
                Set start and end times before this promotion can go live.
              </Alert>
            )}

            {isEditor ? (
              <PromotionForm
                requireDates={Boolean(isDraft)}
                saving={saving}
                isNewPromotion={false}
                originalIdentifier={promotion.identifier}
                initialValues={{
                  title: promotion.title,
                  identifier: promotion.identifier,
                  storyUuid: promotion.storyUuid || '',
                  disabled: promotion.disabled,
                  startTime: promotion.startTime,
                  endTime: promotion.endTime,
                }}
                onSubmit={handleSubmit}
                onUnsavedPromoTypeFieldsChange={setHasUnsavedPromoTypeFields}
              />
            ) : (
              <dl className="row mb-0">
                <dt className="col-sm-3">Identifier</dt>
                <dd className="col-sm-9">{promotion.identifier}</dd>
                <dt className="col-sm-3">Story UUID</dt>
                <dd className="col-sm-9">{promotion.storyUuid || '—'}</dd>
                <dt className="col-sm-3">Start time</dt>
                <dd className="col-sm-9">
                  {promotion.startTime ? (
                    <Moment format="YYYY-MM-DD hh:mm A">{promotion.startTime}</Moment>
                  ) : '—'}
                </dd>
                <dt className="col-sm-3">End time</dt>
                <dd className="col-sm-9">
                  {promotion.endTime ? (
                    <Moment format="YYYY-MM-DD hh:mm A">{promotion.endTime}</Moment>
                  ) : '—'}
                </dd>
                <dt className="col-sm-3">Disabled</dt>
                <dd className="col-sm-9">{promotion.disabled ? 'Yes' : 'No'}</dd>
              </dl>
            )}
          </Card.Body>
        </Card>

        <Card className="mt-3">
          <Card.Body>
            <PromotionTypeTabs
              key={promotion.id}
              subcategories={subcategories}
              configuredTags={promotion.tags}
              dirtyKeys={dirtyPromoTypeKeys}
              dimmed={hasUnsavedPromoTypeFields}
              renderTabPanel={(subcategory) => {
                if (subcategory.key === REWARDS_PROMO_TYPE_KEY) {
                  return (
                    <RewardBannerEditor
                      promotion={promotion}
                      isEditor={isEditor}
                      onSaved={loadPromotion}
                      onDirtyChange={(dirty) => handlePromoTypeDirtyChange(subcategory.key, dirty)}
                    />
                  );
                }
                if (subcategory.key === CONTESTS_PROMO_TYPE_KEY) {
                  return (
                    <ContestEditor
                      promotion={promotion}
                      isEditor={isEditor}
                      onSaved={loadPromotion}
                      onDirtyChange={(dirty) => handlePromoTypeDirtyChange(subcategory.key, dirty)}
                    />
                  );
                }
                if (subcategory.key === SALES_PROMO_TYPE_KEY) {
                  return (
                    <SalesEditor
                      promotion={promotion}
                      isEditor={isEditor}
                      onSaved={loadPromotion}
                      onDirtyChange={(dirty) => handlePromoTypeDirtyChange(subcategory.key, dirty)}
                    />
                  );
                }
                return undefined;
              }}
            />
          </Card.Body>
        </Card>
      </>
    );
  }

  return (
    <PageWrapper user={user} page={pageLayout}>
      {bodyContent}
    </PageWrapper>
  );
}
